/**
 * External dependencies
 */
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { ReactNode } from 'react';
/**
 * Internal dependencies
 */
import { getApiErrorStatus, shouldRetryApiError } from '../utils';
import { globalErrorManager } from './global-error-manager';

// Both the retry policy and the global error detection below read the HTTP
// status, which apiFetch drops on its way to throwing the parsed body.
// `fetchPreservingStatus()` restores it for every request that goes through the
// stats transport or the notices route by parsing at its own call site — nothing
// needs registering here, so neither depends on the app's boot path. The few
// queries still on bare apiFetch (latest post, product images, site sync) hit
// local WP REST routes, whose `WP_Error` bodies already carry `data.status`.

const DEFAULT_STALE_TIME = 5 * 60 * 1000;
const DEFAULT_GC_TIME = 10 * 60 * 1000;

/**
 * QueryCache with global error detection for auth and server errors.
 *
 * Error codes handled:
 * - 401: Authentication failure (session expired, invalid token)
 * - 502: Bad gateway (proxy/load balancer can't reach upstream)
 * - 503: Service unavailable (server overloaded or under maintenance)
 * - 504: Gateway timeout (request took too long)
 *
 * Module level is safe: this is QueryClient configuration rather than a side-effect
 * subscription, and QueryClient must be instantiated once. The error state itself is
 * consumed via useSyncExternalStore in GlobalErrorProvider, which also handles
 * network errors via onlineManager.
 */
const queryCache = new QueryCache( {
	onError: error => {
		const currentError = globalErrorManager.getError();

		// Don't override network error (highest priority)
		if ( currentError === 'network' ) {
			return;
		}

		const status = getApiErrorStatus( error );

		if ( status === 401 ) {
			// Auth errors take precedence over server errors, but not network errors.
			if ( currentError !== 'auth' ) {
				globalErrorManager.setError( 'auth' );
			}
		} else if ( status === 502 || status === 503 || status === 504 ) {
			// Server errors: only set if no higher-priority error exists.
			if ( currentError !== 'auth' && currentError !== 'server' ) {
				globalErrorManager.setError( 'server' );
			}
		}
	},
	onSuccess: () => {
		// Clear transient server errors once queries start succeeding again.
		if ( globalErrorManager.getError() === 'server' ) {
			globalErrorManager.clearError();
		}
	},
} );

export const queryClient = new QueryClient( {
	queryCache,
	defaultOptions: {
		queries: {
			staleTime: DEFAULT_STALE_TIME,

			gcTime: DEFAULT_GC_TIME,

			/**
			 * Noop fetcher to prevent react-query errors for empty queries in console.
			 */
			queryFn: () => Promise.resolve( undefined ),

			/**
			 * 401/403 responses are deterministic for the current user/session.
			 * Retrying them keeps initial widgets in a loading state and delays the
			 * specific auth/plan-gated error UI.
			 */
			retry: shouldRetryApiError,
		},
	},
} );

export const AnalyticsQueryClientProvider = ( { children }: { children: ReactNode } ) => {
	return <QueryClientProvider client={ queryClient }>{ children }</QueryClientProvider>;
};
